/**
 * Tells a *transient* GoTrue failure apart from a genuinely dead session.
 *
 * WHY THIS EXISTS – the "logged out 24 times in 85 minutes" incident
 * (2026-08-28): every auth failure was treated as proof that the session was
 * invalid, so the middleware deleted the `sb-*-auth-token` cookies and the tab
 * resume handler called clearAuthSession(). But `/auth/v1/user` also fails for
 * reasons that say nothing about the session:
 *
 *   - 429 `over_request_rate_limit` – GoTrue rate-limits per IP. Ukrainian
 *     mobile carriers put many subscribers behind one CGNAT address, and a user
 *     with several tabs open trips the limit on their own. One user hit this 25
 *     times in 85 minutes; each hit destroyed a perfectly valid session and
 *     forced another sign-in, which produced more 429s.
 *   - 502/503/504 and network/abort failures – the Supabase custom domain edge
 *     can stall for seconds, and our own 20s timeoutFetch aborts hung requests.
 *
 * On any of these the correct behaviour is to KEEP the session and let the next
 * request revalidate. Only a verdict from GoTrue that the token itself is bad
 * (invalid JWT, missing/reused refresh token, unparseable cookie) may clear it.
 */

export interface AuthErrorLike {
  status?: number
  code?: string
  name?: string
  message?: string
}

/** GoTrue error codes that mean "slow down", not "your session is invalid". */
const TRANSIENT_CODES = new Set([
  'over_request_rate_limit',
  'over_email_send_rate_limit',
  'over_sms_send_rate_limit',
  'request_timeout',
  // Not from GoTrue: synthesized by the middleware when getClaims() throws
  // something that is not a cookie-decode failure (a WebCrypto DOMException,
  // say). The verification could not be performed – that is not a verdict on
  // the session. See src/lib/supabase/middleware.ts.
  'auth_verification_unavailable',
])

/**
 * True when the failure is transient (rate limit, upstream outage, network or
 * timeout) and the caller must therefore preserve the existing session.
 *
 * Defaults to `false` for anything unrecognised: an unknown error still takes
 * the existing clear-the-session path, so this can only ever make the app
 * *less* eager to log someone out, never more.
 */
export function isTransientAuthError(error: AuthErrorLike | null | undefined): boolean {
  if (!error) return false

  if (error.code && TRANSIENT_CODES.has(error.code)) return true

  // supabase-js wraps network failures, aborts and 502/503/504 in this class.
  if (error.name === 'AuthRetryableFetchError') return true
  if (error.name === 'AbortError' || error.name === 'TimeoutError') return true

  const status = error.status
  if (typeof status === 'number') {
    // status 0 is what auth-js assigns when `fetch` itself rejected.
    if (status === 0) return true
    if (status === 429) return true
    if (status >= 500) return true
  }

  return false
}
